/**
 * PostgREST accessors for shift_templates — saved schedule templates for the
 * staff Распоред scheduler (owner/manager via RLS). `entries` is a JSON array of
 * { staff_id, day_offset, start_minutes, end_minutes } applied relative to the
 * target period's start when a template is loaded.
 */
import type { Database, Json } from '@gym/db-types';
import { ApiError } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

export type TemplateScope = 'day' | 'week' | 'month';

export interface TemplateEntry {
  staff_id: string;
  day_offset: number;
  start_minutes: number;
  end_minutes: number;
}

export interface ShiftTemplate {
  id: string;
  name: string;
  scope: TemplateScope;
  entries: TemplateEntry[];
  created_at: string;
}

type TemplateInsert = Database['public']['Tables']['shift_templates']['Insert'];

/** List saved templates for the tenant, optionally filtered to a scope. */
export async function listShiftTemplates(
  client: GymSupabaseClient,
  tenantId: string,
  scope?: TemplateScope,
): Promise<ShiftTemplate[]> {
  let q = client
    .from('shift_templates')
    .select('id, name, scope, entries, created_at')
    .eq('tenant_id', tenantId)
    .order('name');
  if (scope) q = q.eq('scope', scope);
  const { data, error } = await q;
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    scope: r.scope as TemplateScope,
    entries: (r.entries ?? []) as unknown as TemplateEntry[],
    created_at: r.created_at,
  }));
}

export interface CreateTemplateInput {
  name: string;
  scope: TemplateScope;
  entries: TemplateEntry[];
}

/** Save a new template (owner/manager via RLS). */
export async function createShiftTemplate(
  client: GymSupabaseClient,
  tenantId: string,
  input: CreateTemplateInput,
): Promise<{ id: string }> {
  const row: TemplateInsert = {
    tenant_id: tenantId,
    name: input.name,
    scope: input.scope,
    entries: input.entries as unknown as Json,
  };
  const { data, error } = await client.from('shift_templates').insert(row).select('id').single();
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return data;
}

/** Delete a template (owner/manager via RLS). */
export async function deleteShiftTemplate(client: GymSupabaseClient, id: string): Promise<void> {
  const { error } = await client.from('shift_templates').delete().eq('id', id);
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
}
